import { useState, useEffect, useCallback } from 'react';
import {
  Wifi, WifiOff, RefreshCw, Shield, AlertTriangle,
  DollarSign, Briefcase, Activity, Server, Info,
  CheckCircle, XCircle, Clock,
} from 'lucide-react';
import { brokerAPI, type BrokerAccount } from '../../api/broker';

// ─── Styles ────────────────────────────────────────────────
const card: React.CSSProperties = {
  background: '#1e293b80',
  border: '1px solid #334155',
  borderRadius: '12px',
  padding: '20px',
  marginBottom: '20px',
};

const sectionTitle: React.CSSProperties = {
  color: '#10b981',
  fontWeight: 600,
  marginBottom: '16px',
  marginTop: 0,
  fontSize: '16px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const labelStyle: React.CSSProperties = {
  color: '#94a3b8',
  fontSize: '13px',
  display: 'block',
  marginBottom: '4px',
};

const valueStyle: React.CSSProperties = {
  color: 'white',
  fontWeight: 500,
  fontSize: '15px',
};

const gridTwo: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
};

const gridThree: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr 1fr',
  gap: '16px',
};

const buttonPrimary: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 20px',
  background: '#3b82f6',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: 500,
  fontSize: '14px',
  transition: 'all 0.2s',
};

const badge = (bg: string, fg: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '3px 12px',
  borderRadius: '20px',
  fontSize: '12px',
  fontWeight: 600,
  background: bg,
  color: fg,
});

// ─── Helpers ───────────────────────────────────────────────
function formatCurrency(v: string | number | undefined): string {
  if (v === undefined || v === null) return '--';
  const num = typeof v === 'string' ? parseFloat(v) : v;
  if (isNaN(num)) return '--';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

// ─── Component ─────────────────────────────────────────────
export default function BrokerSettings() {
  const [account, setAccount] = useState<BrokerAccount | null>(null);
  const [positionsCount, setPositionsCount] = useState<number | null>(null);
  const [openOrdersCount, setOpenOrdersCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchAccountData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [acct, positions, orders] = await Promise.all([
        brokerAPI.getAccount(),
        brokerAPI.getPositions().catch(() => ({ positions: [], count: 0 })),
        brokerAPI.getOrders('open', 100).catch(() => ({ orders: [], count: 0 })),
      ]);
      setAccount(acct);
      setPositionsCount(positions.count);
      setOpenOrdersCount(orders.count);
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to load account data');
      setAccount(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccountData();
  }, [fetchAccountData]);

  const handleTestConnection = async () => {
    try {
      setTestLoading(true);
      setTestResult(null);
      const result = await brokerAPI.testConnection();
      setTestResult({
        success: true,
        message: result.message || 'Connection successful',
      });
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.message || 'Connection failed',
      });
    } finally {
      setTestLoading(false);
    }
  };

  const connected = !!account && !error;
  const isPaper = account?.paper ?? true;

  return (
    <div style={{ padding: '24px', minHeight: '100vh', background: '#0a0e27' }}>
      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '12px',
            background: '#3b82f622', border: '1px solid #3b82f644',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Briefcase size={24} style={{ color: '#3b82f6' }} />
          </div>
          <div>
            <h1 style={{ color: 'white', margin: 0, fontSize: '24px', fontWeight: 700 }}>Broker Settings</h1>
            <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '14px' }}>
              Alpaca brokerage connection and account overview
            </p>
          </div>
        </div>

        {/* Refresh + last updated */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {lastRefresh && (
            <span style={{ color: '#64748b', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Clock size={12} /> Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchAccountData}
            disabled={loading}
            style={{
              ...buttonPrimary,
              opacity: loading ? 0.6 : 1,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            <RefreshCw size={16} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '960px' }}>
        {/* ── Connection Status ──────────────────────────── */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {connected ? (
                <Wifi size={20} style={{ color: '#10b981' }} />
              ) : (
                <WifiOff size={20} style={{ color: '#ef4444' }} />
              )}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    width: '10px', height: '10px', borderRadius: '50%',
                    background: connected ? '#10b981' : '#ef4444',
                    display: 'inline-block',
                    boxShadow: connected ? '0 0 8px #10b98188' : '0 0 8px #ef444488',
                  }} />
                  <span style={{ color: 'white', fontWeight: 600, fontSize: '15px' }}>
                    {connected ? 'Connected to Alpaca' : 'Disconnected'}
                  </span>
                </div>
                {error && (
                  <span style={{ color: '#f87171', fontSize: '13px', marginTop: '4px', display: 'block' }}>
                    {error}
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              {/* Paper / Live badge */}
              {connected && (
                <span style={badge(
                  isPaper ? '#f59e0b22' : '#10b98122',
                  isPaper ? '#fbbf24' : '#34d399',
                )}>
                  {isPaper ? (
                    <><AlertTriangle size={12} /> Paper Trading</>
                  ) : (
                    <><CheckCircle size={12} /> Live Trading</>
                  )}
                </span>
              )}

              {/* Test Connection */}
              <button
                onClick={handleTestConnection}
                disabled={testLoading}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  background: '#10b98122',
                  color: '#34d399',
                  border: '1px solid #10b98144',
                  borderRadius: '8px',
                  cursor: testLoading ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                  fontSize: '13px',
                  opacity: testLoading ? 0.6 : 1,
                  transition: 'all 0.2s',
                }}
              >
                <Activity size={14} style={{ animation: testLoading ? 'spin 1s linear infinite' : 'none' }} />
                {testLoading ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
          </div>

          {/* Test result message */}
          {testResult && (
            <div style={{
              marginTop: '12px',
              padding: '10px 14px',
              borderRadius: '8px',
              background: testResult.success ? '#10b98115' : '#ef444415',
              border: `1px solid ${testResult.success ? '#10b98133' : '#ef444433'}`,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              color: testResult.success ? '#34d399' : '#f87171',
            }}>
              {testResult.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
              {testResult.message}
            </div>
          )}
        </div>

        {/* ── Account Info (only when connected) ─────────── */}
        {connected && account && (
          <>
            {/* Financial Overview */}
            <div style={card}>
              <h3 style={sectionTitle}>
                <DollarSign size={18} /> Account Overview
              </h3>
              <div style={gridThree}>
                <div>
                  <span style={labelStyle}>Status</span>
                  <span style={{
                    ...badge(
                      account.status === 'ACTIVE' ? '#10b98122' : '#f59e0b22',
                      account.status === 'ACTIVE' ? '#34d399' : '#fbbf24',
                    ),
                    fontSize: '13px',
                  }}>
                    {account.status}
                  </span>
                </div>
                <div>
                  <span style={labelStyle}>Equity</span>
                  <span style={{ ...valueStyle, color: '#60a5fa' }}>
                    {formatCurrency(account.equity)}
                  </span>
                </div>
                <div>
                  <span style={labelStyle}>Cash</span>
                  <span style={valueStyle}>{formatCurrency(account.cash)}</span>
                </div>
                <div>
                  <span style={labelStyle}>Buying Power</span>
                  <span style={{ ...valueStyle, color: '#34d399' }}>
                    {formatCurrency(account.buying_power)}
                  </span>
                </div>
                <div>
                  <span style={labelStyle}>Portfolio Value</span>
                  <span style={valueStyle}>{formatCurrency(account.portfolio_value)}</span>
                </div>
                <div>
                  <span style={labelStyle}>Currency</span>
                  <span style={valueStyle}>{account.currency || 'USD'}</span>
                </div>
              </div>

              {/* Day Trade Count + PDT Warning */}
              <div style={{ marginTop: '16px', padding: '12px 16px', background: '#0f172a', borderRadius: '8px', border: '1px solid #1e293b' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <span style={labelStyle}>Day Trades Used (rolling 5 days)</span>
                    <span style={{
                      ...valueStyle,
                      color: account.daytrade_count >= 3 ? '#fbbf24' : '#e2e8f0',
                      fontSize: '18px',
                    }}>
                      {account.daytrade_count} / 3
                    </span>
                  </div>
                  {account.daytrade_count >= 3 && (
                    <span style={badge('#f59e0b22', '#fbbf24')}>
                      <AlertTriangle size={14} />
                      PDT Warning &mdash; approaching limit
                    </span>
                  )}
                  {account.pattern_day_trader && (
                    <span style={badge('#ef444422', '#f87171')}>
                      <AlertTriangle size={14} />
                      Pattern Day Trader flagged
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div style={{ ...card, display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{
                flex: '1 1 200px',
                padding: '16px',
                background: '#0f172a',
                borderRadius: '10px',
                border: '1px solid #1e293b',
                textAlign: 'center',
              }}>
                <Briefcase size={20} style={{ color: '#60a5fa', marginBottom: '6px' }} />
                <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Open Positions</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 700 }}>
                  {positionsCount !== null ? positionsCount : '--'}
                </div>
              </div>
              <div style={{
                flex: '1 1 200px',
                padding: '16px',
                background: '#0f172a',
                borderRadius: '10px',
                border: '1px solid #1e293b',
                textAlign: 'center',
              }}>
                <Activity size={20} style={{ color: '#a78bfa', marginBottom: '6px' }} />
                <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Open Orders</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 700 }}>
                  {openOrdersCount !== null ? openOrdersCount : '--'}
                </div>
              </div>
              <div style={{
                flex: '1 1 200px',
                padding: '16px',
                background: '#0f172a',
                borderRadius: '10px',
                border: '1px solid #1e293b',
                textAlign: 'center',
              }}>
                <DollarSign size={20} style={{ color: '#34d399', marginBottom: '6px' }} />
                <div style={{ color: '#94a3b8', fontSize: '12px', marginBottom: '4px' }}>Day Trading BP</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 700 }}>
                  {formatCurrency(account.daytrading_buying_power)}
                </div>
              </div>
            </div>

            {/* Account Restrictions */}
            <div style={card}>
              <h3 style={sectionTitle}>
                <Shield size={18} /> Account Restrictions
              </h3>
              <div style={gridTwo}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {account.trading_blocked ? (
                    <XCircle size={18} style={{ color: '#ef4444' }} />
                  ) : (
                    <CheckCircle size={18} style={{ color: '#10b981' }} />
                  )}
                  <div>
                    <span style={labelStyle}>Trading</span>
                    <span style={{ color: account.trading_blocked ? '#f87171' : '#34d399', fontWeight: 500 }}>
                      {account.trading_blocked ? 'Blocked' : 'Enabled'}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {account.transfers_blocked ? (
                    <XCircle size={18} style={{ color: '#ef4444' }} />
                  ) : (
                    <CheckCircle size={18} style={{ color: '#10b981' }} />
                  )}
                  <div>
                    <span style={labelStyle}>Transfers</span>
                    <span style={{ color: account.transfers_blocked ? '#f87171' : '#34d399', fontWeight: 500 }}>
                      {account.transfers_blocked ? 'Blocked' : 'Enabled'}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {account.account_blocked ? (
                    <XCircle size={18} style={{ color: '#ef4444' }} />
                  ) : (
                    <CheckCircle size={18} style={{ color: '#10b981' }} />
                  )}
                  <div>
                    <span style={labelStyle}>Account Access</span>
                    <span style={{ color: account.account_blocked ? '#f87171' : '#34d399', fontWeight: 500 }}>
                      {account.account_blocked ? 'Blocked' : 'Active'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Alpaca Connection Info ─────────────────────── */}
        <div style={card}>
          <h3 style={sectionTitle}>
            <Server size={18} /> Alpaca Connection
          </h3>
          <div style={gridTwo}>
            <div>
              <span style={labelStyle}>API Key</span>
              <span style={{
                color: '#94a3b8',
                fontFamily: 'monospace',
                fontSize: '13px',
                background: '#0f172a',
                padding: '6px 12px',
                borderRadius: '6px',
                display: 'inline-block',
                border: '1px solid #1e293b',
              }}>
                *** configured via server env ***
              </span>
            </div>
            <div>
              <span style={labelStyle}>API Secret</span>
              <span style={{
                color: '#94a3b8',
                fontFamily: 'monospace',
                fontSize: '13px',
                background: '#0f172a',
                padding: '6px 12px',
                borderRadius: '6px',
                display: 'inline-block',
                border: '1px solid #1e293b',
              }}>
                *** configured via server env ***
              </span>
            </div>
            <div>
              <span style={labelStyle}>Base URL</span>
              <span style={{
                color: '#94a3b8',
                fontFamily: 'monospace',
                fontSize: '13px',
                background: '#0f172a',
                padding: '6px 12px',
                borderRadius: '6px',
                display: 'inline-block',
                border: '1px solid #1e293b',
              }}>
                {isPaper
                  ? 'https://paper-api.alpaca.markets'
                  : 'https://api.alpaca.markets'}
              </span>
            </div>
            {connected && account?.account_number && (
              <div>
                <span style={labelStyle}>Account Number</span>
                <span style={{
                  color: '#94a3b8',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  background: '#0f172a',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  display: 'inline-block',
                  border: '1px solid #1e293b',
                }}>
                  {account.account_number}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Paper / Live Mode Explanation ──────────────── */}
        <div style={{
          ...card,
          background: '#0f172a',
          border: '1px solid #1e293b',
        }}>
          <h3 style={{ ...sectionTitle, color: '#60a5fa' }}>
            <Info size={18} /> Paper vs. Live Trading
          </h3>
          <div style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.7' }}>
            <p style={{ margin: '0 0 10px' }}>
              <strong style={{ color: '#fbbf24' }}>Paper Mode</strong> uses Alpaca's simulated
              environment with virtual funds. All trades execute against real market data but no
              real money is at risk. Ideal for testing strategies and verifying integrations.
            </p>
            <p style={{ margin: '0 0 10px' }}>
              <strong style={{ color: '#34d399' }}>Live Mode</strong> connects to your funded
              Alpaca brokerage account. Orders are routed to real exchanges and executed with
              real capital. Ensure your strategy is thoroughly tested before switching.
            </p>
            <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>
              The trading mode is determined by the API keys configured on the server
              (<code style={{ color: '#94a3b8', background: '#1e293b', padding: '1px 6px', borderRadius: '4px' }}>APCA_API_KEY_ID</code> and
              {' '}<code style={{ color: '#94a3b8', background: '#1e293b', padding: '1px 6px', borderRadius: '4px' }}>APCA_API_SECRET_KEY</code>).
              To switch modes, update the environment variables on the backend and redeploy.
            </p>
          </div>
        </div>
      </div>

      {/* Spin animation for refresh icon */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
